(() => {
  "use strict";

  const isPrimaryNavigation = event => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  const normalizeAccountLink = () => {
    document.querySelectorAll("a.header__icon--account").forEach(link => {
      link.href = "/account";
      link.removeAttribute("aria-haspopup");
    });
  };

  const updateCartCount = cart => {
    const count = Number(cart?.totalQuantity ?? cart?.item_count);
    if (!Number.isSafeInteger(count) || count < 0) return;
    document.querySelectorAll("#cart-icon-bubble").forEach(link => {
      let bubble = link.querySelector(".cart-count-bubble");
      if (!count) { bubble?.remove(); return; }
      if (!bubble) {
        bubble = document.createElement("div");
        bubble.className = "cart-count-bubble";
        const visible = document.createElement("span");
        visible.setAttribute("aria-hidden", "true");
        const accessible = document.createElement("span");
        accessible.className = "visually-hidden";
        bubble.append(visible, accessible);
        link.appendChild(bubble);
      }
      const [visible, accessible] = bubble.querySelectorAll("span");
      if (visible) visible.textContent = count < 100 ? String(count) : "";
      if (accessible) accessible.textContent = `${count} ${count === 1 ? "item" : "items"}`;
    });
  };

  const addonProductRules = [
    ["cardfamous-classic-edition", "addonImageClassic"],
    ["cardfamous-addon-qr-authentication", "addonImageQr"],
    ["cardfamous-addon-magnetic-display-stand", "addonImageStand"],
    ["cardfamous-addon-gift-packaging", "addonImageGift"],
    ["cardfamous-addon-collector-certificate", "addonImageCertificate"],
    ["cardfamous-addon-serialized-numbering", "addonImageSerial"],
    ["cardfamous-addon-gold-foil", "addonImageGold"],
  ];

  const findAddonRule = link => {
    try {
      const pathname = new URL(link.href, location.origin).pathname;
      const slug = decodeURIComponent(pathname.split("/products/")[1]?.split("/")[0] || "");
      return addonProductRules.find(([candidate]) => candidate === slug) || null;
    } catch {
      return null;
    }
  };

  const fallbackForImage = (image, fallback) => {
    if (!fallback) return;
    const useFallback = () => {
      if (image.dataset.cardfamousAddonFallback === fallback) return;
      image.dataset.cardfamousAddonFallback = fallback;
      image.src = fallback;
      image.removeAttribute("srcset");
    };
    if (!image.getAttribute("src") || (image.complete && image.naturalWidth === 0)) useFallback();
    else if (image.dataset.cardfamousAddonFallbackBound !== "1") {
      image.dataset.cardfamousAddonFallbackBound = "1";
      image.addEventListener("error", useFallback);
    }
  };

  const findCatalogCard = link => link.closest(
    ".card-wrapper, .grid-product, .product-card, .product-item, [data-product-card], [data-product-handle], [data-product-id]"
  ) || (link.closest(".recently-viewed, .recently-viewed-products, .recently-viewed-grid") ? link.closest("li") : null);

  const hideCreatorOnlyCatalogCards = images => {
    document.querySelectorAll('a[href*="/products/"]').forEach(link => {
      if (link.closest("#cardfamous-creator, .cart-item, cart-items, [data-cart-item]")) return;
      const rule = findAddonRule(link);
      if (!rule) return;
      const card = findCatalogCard(link);
      if (!card) return;
      card.setAttribute("data-cardfamous-creator-only-card", "true");
      const fallback = images?.[rule[1]] || "";
      card.querySelectorAll("img").forEach(image => fallbackForImage(image, fallback));
    });
  };

  const ensureCreatorOnlyNotice = (anchor, title) => {
    if (!anchor || document.querySelector("[data-cardfamous-creator-only-notice]")) return;
    const notice = document.createElement("div");
    notice.setAttribute("data-cardfamous-creator-only-notice", "true");
    notice.innerHTML = `<p><strong>${title}</strong> is made from a completed CardFamous collectible.</p><a href="/?cardfamous_mode=card#cardfamous-creator">Create your collectible first →</a>`;
    (anchor.closest(".product__info-container, .product__info-wrapper") || anchor.parentElement)?.insertBefore(notice, anchor);
  };

  const ensureCreatorOnlyPage = (title, fallback) => {
    const main = document.querySelector("#MainContent");
    if (!main) return;
    const existing = document.querySelector("[data-cardfamous-creator-only-page]");
    if (existing) {
      if (!main.contains(existing)) (main.parentElement || document.body).insertBefore(existing, main);
      return;
    }
    const info = document.querySelector(".product__info-container");
    const hidden = info && (getComputedStyle(info).display === "none" || info.getBoundingClientRect().height === 0);
    if (!hidden) return;
    const panel = document.createElement("section");
    panel.setAttribute("data-cardfamous-creator-only-page", "true");
    panel.innerHTML = `<img src="${fallback}" alt="${title}" loading="eager"><div><p><strong>${title}</strong> is made from a completed CardFamous collectible.</p><a href="/?cardfamous_mode=card#cardfamous-creator">Create your collectible first →</a></div>`;
    // Keep the guard outside Shopify's replaceable product-section subtree;
    // some themes rerender #MainContent after load and would otherwise remove
    // the call-to-action immediately after it is inserted.
    (main.parentElement || document.body).insertBefore(panel, main);
  };

  // Shopify can apply the final product-section visibility state after this
  // extension script runs. Retry after layout settles so creator-only
  // products never render as an empty page when the native product-info
  // wrapper is hidden by the theme/runtime.
  const scheduleCreatorOnlyPage = (title, fallback) => {
    ensureCreatorOnlyPage(title, fallback);
    const main = document.querySelector("#MainContent");
    if (!main || main.querySelector("[data-cardfamous-creator-only-page]")) return;
    const key = `${title}|${fallback}`;
    if (main.dataset.cardfamousCreatorOnlyPageScheduled === key) return;
    main.dataset.cardfamousCreatorOnlyPageScheduled = key;
    let attempts = 0;
    const retry = () => {
      ensureCreatorOnlyPage(title, fallback);
      if (main.querySelector("[data-cardfamous-creator-only-page]") || attempts++ >= 4) {
        delete main.dataset.cardfamousCreatorOnlyPageScheduled;
        return;
      }
      window.setTimeout(retry, attempts === 1 ? 250 : 500);
    };
    requestAnimationFrame(retry);
  };

  const installAddonImageFallbacks = () => {
    const source = document.querySelector("[data-cardfamous-addon-images]");
    if (!source) return;
    const images = Object.fromEntries(addonProductRules.map(([, key]) => [key, source.dataset[key] || ""]));
    document.querySelectorAll(".cart-item, cart-items li, [data-cart-item], .product-form").forEach(row => {
      const productLink = [...row.querySelectorAll("a[href]")].find(link => findAddonRule(link));
      if (!productLink) return;
      const rule = findAddonRule(productLink);
      const fallback = rule ? images[rule[1]] : "";
      row.querySelectorAll("img").forEach(image => fallbackForImage(image, fallback));
    });
    hideCreatorOnlyCatalogCards(images);
    const pageRule = addonProductRules.find(([slug]) => location.pathname.includes(`/products/${slug}`));
    if (pageRule) {
      const fallback = images[pageRule[1]];
      document.querySelectorAll(".product__media img, .product__media-wrapper img, .product__image img, .product-single__media img").forEach(image => fallbackForImage(image, fallback));
      document.querySelectorAll('.product-form, form[action*="/cart/add"], .shopify-payment-button, button[name="add"], input[name="add"]').forEach(control => {
        control.setAttribute("data-cardfamous-creator-only-purchase", "true");
        control.hidden = true;
      });
      const title = pageRule[0].replaceAll("-", " ").replace(/\b\w/g, letter => letter.toUpperCase());
      ensureCreatorOnlyNotice(document.querySelector("[data-cardfamous-creator-only-purchase]"), title);
      scheduleCreatorOnlyPage(title, fallback);
    }
  };

  normalizeAccountLink();
  installAddonImageFallbacks();
  const header = document.querySelector(".shopify-section-group-header-group");
  if (header) new MutationObserver(normalizeAccountLink).observe(header, { subtree: true, attributes: true, attributeFilter: ["href"] });
  const cartObserver = new MutationObserver(installAddonImageFallbacks);
  cartObserver.observe(document.documentElement, { subtree: true, childList: true });
  document.addEventListener("cart:updated", event => { updateCartCount(event.detail); installAddonImageFallbacks(); });

  document.addEventListener("click", event => {
    if (!isPrimaryNavigation(event)) return;
    const account = event.target.closest("a.header__icon--account");
    if (account) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign("/account");
      return;
    }
    const deadProductCta = event.target.closest('.product__description a[href="#"]');
    if (!deadProductCta) return;
    const form = document.querySelector('.product-form form[action*="/cart/add"]');
    const purchaseControl = form?.querySelector('button[type="submit"], input[type="submit"]');
    if (!form || !purchaseControl) return;
    event.preventDefault();
    form.scrollIntoView({ behavior: "smooth", block: "center" });
    purchaseControl.focus({ preventScroll: true });
  }, true);
})();
